import React, {
	useState,
	useEffect,
	forwardRef,
	FocusEvent,
	ReactElement,
	HTMLAttributes,
} from 'react';
import './InputDropdown.css';
import { IconDownTriangle } from '../../assets/icons';
import Label from '../Label';
import { IToolTipProps } from '../ToolTip/ToolTip';
import SuccessIcon from '../InputBase/SuccessIcon/SuccessIcon';
import DynamicContainer from '../../foundational/Layout/DynamicContainer';

type HTMLInputProps = Omit<HTMLAttributes<HTMLSelectElement>, 'onFocus'|'onChange'|'onBlur'>;
export interface IInputDropdownProps extends HTMLInputProps {
	name: string;
	required?: boolean;
	label?: string | ReactElement | ReactElement[];
	helperText?: string | ReactElement | ReactElement[];
	error?: boolean;
	errorText?: string;
	disabled?: boolean;
	className?: string;
	labelClass?: string;
	inputClass?: string;
	value?: string;
	description?: string | ReactElement | ReactElement[];
	tooltip?: string | ReactElement | ReactElement[];
	valid?: [boolean, (arg?: any) => void];
	hideAlertIcon?: boolean;
	invalidErrorMessage?: string;
	placeholder?: string;
	id?: string;
	labelAlign?: 'left' | 'center';
	onFocus?: (value: FocusEvent<HTMLSelectElement>) => void;
	onChange?: (value: string) => void;
	onBlur?: (value: FocusEvent<HTMLSelectElement>) => void;
	setValue: (value: string) => void;
	options: string[] | { option: string; value: any }[];
	tooltipNotchPosition?: IToolTipProps['notchPosition'];
	mobileTooltipNotchPosition?: IToolTipProps['notchPosition'];
	noErrorsOrHelper?: boolean;
	alternate?: boolean;
	readOnly?: boolean;
	inputChild?: ReactElement;
	showSuccessIcon?: boolean;
	useSuccessIcon?: boolean;
	showExclamationError?: boolean;
	resetToggle?: boolean;
}

type TOption = IInputDropdownProps['options'][number];

const InputDropdown = forwardRef(
	(
		{
			required = false,
			helperText = '',
			errorText = '',
			disabled = false,
			className = '',
			labelClass = '',
			inputClass = '',
			description = '',
			error = false,
			value,
			label,
			name,
			hideAlertIcon = false,
			labelAlign = 'left',
			invalidErrorMessage = '',
			placeholder = '',
			tooltip = '',
			tooltipNotchPosition = 'bottom-left',
			mobileTooltipNotchPosition = 'bottom-right',
			valid = [true, () => { }],
			id = '',
			onFocus = () => { },
			onChange = () => { },
			onBlur = () => { },
			setValue = () => { },
			options = [],
			noErrorsOrHelper = false,
			alternate = false,
			readOnly = false,
			inputChild,
			showSuccessIcon = false,
			useSuccessIcon = false,
			showExclamationError = false,
			resetToggle = false,
			...rest
		}: IInputDropdownProps,
		ref: any,
	) => {
		const [selected, setSelected] = useState<string>();
		const [focused, setFocused] = useState(false);
		const [visited, setVisited] = useState(false);
		const [requiredErr, setRequiredErr] = useState(false);
		const validate = () => {
			if (visited && !focused) {
				if (required) {
					if (value) {
						setRequiredErr(false);
						valid[1](true);
					} else {
						setRequiredErr(true);
						valid[1](false);
					}
				}
			} else {
				setRequiredErr(false);
				valid[1](true);
			}
		};

		const resetVisitedAndFocused = () => {
			setFocused(false);
			setVisited(false);
		}

		const updateSelected = (
			list: IInputDropdownProps['options'],
			val: number,
		) => {
			if (val > -1) {
				const rawValue = list[val];
				const selectedVal =
					typeof rawValue === 'object' ? rawValue.value : rawValue;
				setSelected(selectedVal);
			}
		};

		useEffect(() => {
		 if (!focused && visited) {
		  validate();
		 }
		}, [focused, value]);

		useEffect(() => {
			resetVisitedAndFocused();
		}, [resetToggle])
	
	useEffect(() => {
		if (!focused) {
			validate();
		}
	}, [visited]);

		useEffect(() => {
		if (onChange && selected) {
		onChange(selected);
		}
		}, [selected]);

		const utilityPTag = () => {
			const pid = `${id || label}__input__helper`;
			if (noErrorsOrHelper) {
				if (description) {
					return (
						<p
							className={`${!alternate ? 'Legacy-' : ''
								}InputDropdown__description`}
							aria-live='assertive'
							id={pid}
						>
							{description}
						</p>
					);
				}
				return null;
			}
			return (
				<p
					className={`${
						!alternate ? 'Legacy-' : ''
					}InputDropdown__description${
						error || requiredErr
							? `--error${showExclamationError ? 'WithExclamation': ''}`
							: disabled
							? '--disabled'
							: ''
					}`}
					aria-live='assertive'
					id={pid}
				>
					{requiredErr
						? invalidErrorMessage || `${label} is required.`
						: error
						? errorText
						: description}
				</p>
			);
		};

		return (
			<DynamicContainer displayTag={useSuccessIcon} tag='div' className='InputDropdown__wrapperContainer'>
				<label
					className={`${!alternate ? 'Legacy-' : ''
						}InputDropdown ${className} block`}
				>
					<Label
						helperText={helperText}
						className={labelClass}
						tooltip={tooltip}
						error={requiredErr || error}
						tooltipAlign={tooltipNotchPosition}
						mobileTooltipAlign={mobileTooltipNotchPosition}
						required={required}
						alternate={alternate}
					>
						{label}
					</Label>
					<div
						className={`${!alternate ? 'Legacy-' : ''
							}InputDropdown__selectWrapper${readOnly
								? '--readOnly'
								: disabled
									? '--disabled'
									: error || requiredErr
										? '--error'
										: ''
							}`}
					>
						<select
								id={id}
								name={name}
								disabled={disabled}
								className={`${!alternate ? 'Legacy-' : ''
									}InputDropdown__select${readOnly
										? '--readOnly'
										: error || requiredErr
											? '--error'
											: ''
									} ${(error || requiredErr) && !hideAlertIcon
										? 'right_padding'
										: ''
									}` + (inputClass ? ` ${inputClass}` : '')}
								onFocus={(e) => {
									setVisited(true);
									setFocused(true);
									onFocus(e);
								}}
								onBlur={(e) => {
									setFocused(false);
									onBlur(e);
									validate();
								}}
								value={value}
								onChange={(e) => {
									const selection = e.target.value; // Collects the value of the selected option.

									// Iterate through each element in the options array in order to find the one corresponding to the selection.
									for (const [index, element] of options.entries()) {
										// If the element is a key-value pair...
										if (typeof element === 'object') {
											if (element.value === selection) {
												setValue(element.value);
												onChange(element.value);
												updateSelected(options, index);
												validate();
											}
										} else {
											if (element === selection) {
												setValue(element);
												onChange(element);
												updateSelected(options, index);
												validate();
											}
										}
									}
								}}
								ref={ref}
								{...rest}
							>
								<option
									selected
									disabled
									hidden
									className={`${!alternate ? 'Legacy-' : ''
										}InputDropdown__select__option`}
									value=''
								>
									{placeholder}
								</option>
								{options &&
									(options as any).map((val: TOption, i: number) => {
										return (
											<option
												className={`${!alternate ? 'Legacy-' : ''
													}InputDropdown__select__option`}
												key={i}
												value={
													typeof val === 'object'
														? val.value
														: val
												}
											>
												{typeof val === 'object'
													? val.option
													: val}
											</option>
										);
									})}
							</select>
							
							{!readOnly && (
								<IconDownTriangle
									className={`${!alternate ? 'Legacy-' : ''
										}InputDropdown__icon`}
								/>
							)}
						{inputChild}
				
					</div>
				
					{utilityPTag()}
				</label>
			{useSuccessIcon &&
				<SuccessIcon showIcon={showSuccessIcon && !focused && value !== ""} />}
			</DynamicContainer>
		);
	},
);
InputDropdown.displayName = 'InputDropdown';

export default InputDropdown;
